import { toReadonlySet, useStableSet } from "utils/sets";
import { useFrozenState } from "utils/useFrozenState";
import { useMemo } from "react";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";

/**
 * List of properties that can be edited by the user. Any properties not listed here will never be able to be changed!
 *
 * We must list these manually because the record we start with could be missing some of them, but they could be added
 * by the user, so we have to account for them.
 */
export const editableProps = [
    "amount",
    "email",
    "phone",
    "requestChannel",
] as const satisfies (keyof BulkRequestRecord)[];

/** The part of a Bulk Request record that can be edited by the user before submission. */
export type BulkRequestRecordEdit = Partial<Pick<BulkRequestRecord, (typeof editableProps)[number]>>;

export type SaveCallback = (id: string, changes: BulkRequestRecordEdit) => void;

/**
 * Build an overlay layer for the data that stores user changes separately.
 * @param data The base data we are overlaying.
 * @returns Overlaid data, plus a callback to apply changes to a record, plus a set of row keys for any patients with
 * communication disabled.
 */
export function useChangeOverlay(data: readonly BulkRequestRecord[] | undefined) {
    // Track changes that the user has made to the data
    // This is overlaid on top of the base queried data
    const [userChanges, setUserChanges] = useFrozenState(new Map<string, BulkRequestRecordEdit>());

    // Overlay user changes on top of queried data
    // This is rendered in the table and used for submission
    const [combinedData, handleSave, withoutCommSet] = useMemo(() => {
        const combinedData = combine(data, userChanges);

        const withoutCommSet = toReadonlySet<string>(
            combinedData.filter((x) => !x.allowCommunication).map((x) => x.pmsId),
        );

        const handleSave: SaveCallback = (id, changes) => {
            setUserChanges((existingChanges) => {
                // Do not modify in-place; React needs to see the change
                const newUserChanges = new Map(existingChanges);

                newUserChanges.set(id, changes);

                return newUserChanges;
            });
        };

        return [combinedData, handleSave, withoutCommSet] as const;
    }, [data, userChanges, setUserChanges]);

    return [combinedData, handleSave, useStableSet(withoutCommSet)] as const;
}

function combine(
    data: readonly BulkRequestRecord[] | undefined,
    userChanges: Readonly<Map<string, BulkRequestRecordEdit>>,
) {
    const baseData = data ?? [];
    return baseData.map<BulkRequestRecord>((baseRecord) =>
        userChanges.has(baseRecord.pmsId)
            ? Object.freeze({
                  ...baseRecord,
                  ...userChanges.get(baseRecord.pmsId),
              })
            : baseRecord,
    );
}
